import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonItemDivider,
  IonLabel,
  IonList,
  IonRow,
  useIonViewDidEnter,
  useIonViewDidLeave,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  setConsentFormSigned,
  setNppFormGiven,
  setFirstName,
  setLastName,
  setMedicalNecessitySigned,
  setMilesToHosp,
  setPcsProviderType,
  setPcsReason,
  setPcsSignedDate,
  setPcsSignedTime,
  savePcsInformation,
} from '../store/pcsInformation.actions'
import { getPcsInformationSelector } from '../selectors'
import { transformDate, transformTime } from '../../../utilities'
import MultiSelect from '../../../components/multiSelect/MultiSelect.component'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import SInput from '../../../components/stringInput/StringInput.component'
import Validation from '../../../components/validation/Validation.component'
import { PcsInformationConstants } from '../store/pcsInformation.constants'
import { getPcsInformationOptionsSelector } from '../../options/selectors'
import { IOption } from '../../../common/common.types'
import { stopwatchOutline } from 'ionicons/icons'
import validationService from '../../validation/services/validation.services'
import './PcsInformationTablet.component.css'
import { numberPadKeysWithDecimal } from '../../../components/numberPad/NumberPad.types'
import { IServiceConfig } from '../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../configurations/selectors/serviceConfig.selector'
import { appendSeconds, truncateSecondsIfNecessary } from '../../../utilities/dateTime.util'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'

const PcsInformationTablet: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isTablet } = useDeviceMetrics()
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const pcsInformationData = useSelector(getPcsInformationSelector)
  const {
    consentFormSigned,
    nppFormGiven,
    medicalNecessitySigned,
    pcsProviderType,
    pcsReason,
    milesToHosp,
    firstName,
    lastName,
    pcsSignedDate,
    pcsSignedTime,
  } = pcsInformationData

  const pcsInformationOptions = useSelector(getPcsInformationOptionsSelector)

  const {
    consentFormSignedOptions,
    medicalNecessitySignedOptions,
    pcsProviderTypeOptions,
    pcsReasonOptions,
    nppFormOptions,
  } = pcsInformationOptions

  const formatNppActiveDate = (dtActive?: string | null) => {
    if (!dtActive) return null
    const [datePart] = dtActive.split('T')
    const [year, month, day] = datePart.split('-')
    if (!year || !month || !day) return null
    return `${month}/${day}/${year}`
  }

  const formatNppOptionLabel = (option?: IOption | null) => {
    if (!option) return option
    const formattedDate = formatNppActiveDate((option as IOption & { dtActive?: string | null }).dtActive)
    if (!formattedDate) return option
    const name = option.name ?? ''
    const formattedLabel = `${formattedDate} - ${name}`
    if (name.startsWith(`${formattedDate} - `)) return option
    return { ...option, name: formattedLabel }
  }

  const isNppOptionActive = (option: IOption) => (option as IOption & { active?: number }).active !== 0

  const formattedNppFormOptions = nppFormOptions
    ?.filter((option) => isNppOptionActive(option))
    .map((option) => formatNppOptionLabel(option))
  const formattedNppFormSelected = formatNppOptionLabel(nppFormGiven)
  const hasNppFormOptions = (formattedNppFormOptions?.length ?? 0) > 0

  const [flag, setFlag] = useState(true)

  const setFirstNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setFirstName(e.detail.value))
    save()
  }

  const setLastNameOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setLastName(e.detail.value))
    save()
  }

  const setConsentFormSignedOnChange = (option: IOption) => {
    dispatch(setConsentFormSigned(option))
    save()
  }

  const setNppFormGivenOnChange = (option: IOption) => {
    dispatch(setNppFormGiven(option))
    save()
  }

  const setMedicalNecessitySignedOnChange = (option: IOption) => {
    dispatch(setMedicalNecessitySigned(option))
    save()
  }

  const setPcsProviderTypeOnChange = (option: IOption) => {
    dispatch(setPcsProviderType(option))
    save()
  }

  const setMilesToHospOnChange = (numbers: string[]) => {
    dispatch(setMilesToHosp(numbers.join('')))
    save()
  }

  const setTimeOnChange = (numbers: string[], pcsSignedTime?: string) => {
    const oldTimeParts = pcsSignedTime?.split('') || []
    if (oldTimeParts.includes(':') && !numbers.includes(':')) {
      numbers.pop()
    }
    const newTime = transformTime(numbers.join(''), displaySeconds)
    dispatch(setPcsSignedTime(newTime))
    save()
  }

  const onBlur = () => {
    const newTime = appendSeconds(pcsSignedTime, displaySeconds)
    dispatch(setPcsSignedTime(newTime))
    save()
  }

  const setCurrentTimeOnChange = () => {
    dispatch({ type: PcsInformationConstants.ADD_CURRENT_TIME })
    save()

    validationService.clearValidation(PcsInformationConstants.SET_PCSSIGNEDTIME)
    validationService.clearValidation(PcsInformationConstants.SET_PCSSIGNEDDATE)
  }

  const dateOnChange = (numbers: string[], pcsSignedDate?: string) => {
    const oldDateParts = pcsSignedDate?.split('') || []
    const oldFwdSlashCount = oldDateParts.filter((o: string) => o === '/').length
    const newFwdSlashCount = numbers.filter((n: string) => n === '/').length
    if (newFwdSlashCount < oldFwdSlashCount) {
      numbers.pop()
    }
    const newDate = transformDate(numbers.join(''))
    dispatch(setPcsSignedDate(newDate))
    save()
  }

  const save = () => {
    dispatch(savePcsInformation())
  }

  useEffect(() => {
    if (flag) {
      consentFormSigned && dispatch(setConsentFormSigned(consentFormSigned))
      nppFormGiven && dispatch(setNppFormGiven(nppFormGiven))
      medicalNecessitySigned && dispatch(setMedicalNecessitySigned(medicalNecessitySigned))
      pcsReason && dispatch(setPcsReason(pcsReason))
      pcsProviderType && dispatch(setPcsProviderType(pcsProviderType))
      firstName && dispatch(setFirstName(firstName))
      lastName && dispatch(setLastName(lastName))
      milesToHosp && dispatch(setMilesToHosp(milesToHosp))
      pcsSignedDate && dispatch(setPcsSignedDate(pcsSignedDate))
      pcsSignedTime && dispatch(setPcsSignedTime(pcsSignedTime))
    }
  }, [flag])

  useIonViewDidEnter(() => {
    setFlag(true)
  })

  useIonViewDidLeave(() => {
    setFlag(false)
  })

  return (
    <>
      <IonItemDivider color='light' className='ion-padding'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('pcsInformation.pcsInformationDetails')}
        </IonLabel>
      </IonItemDivider>

      <IonList id='PcsInformationPageContent' className={isTablet ? 'ion-margin-horizontal' : ''}>
        <IonGrid className='ion-no-padding'>
          {hasNppFormOptions && (
            <>
              <IonRow>
                <IonCol className='picklist'>
                  <SingleSelect
                    dataTestID='nppFormGiven'
                    selectedOption={formattedNppFormSelected}
                    listHeader={t('pcsInformation.nppFormGiven')}
                    options={formattedNppFormOptions}
                    action={setNppFormGivenOnChange}
                    label={t('pcsInformation.nppFormGiven')}
                  />
                </IonCol>
              </IonRow>
              <hr className='field-divider' />
            </>
          )}
          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='consentFormSigned'
                selectedOption={consentFormSigned}
                listHeader={t('pcsInformation.consentFormSigned')}
                options={consentFormSignedOptions}
                action={setConsentFormSignedOnChange}
                label={t('pcsInformation.consentFormSigned')}
              />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='medicalNecessitySigned'
                selectedOption={medicalNecessitySigned}
                listHeader={t('pcsInformation.medicalNecessitySigned')}
                options={medicalNecessitySignedOptions}
                action={setMedicalNecessitySignedOnChange}
                label={t('pcsInformation.medicalNecessitySigned')}
              />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <MultiSelect
                dataTestID='pcsReason'
                listHeader={t('pcsInformation.pcsReason')}
                options={pcsReasonOptions}
                savedValues={pcsReason}
                action={setPcsReason}
                callback={savePcsInformation}
                label={t('pcsInformation.pcsReason')}
              />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='pcsProviderType'
                selectedOption={pcsProviderType}
                listHeader={t('pcsInformation.pcsProviderType')}
                options={pcsProviderTypeOptions}
                action={setPcsProviderTypeOnChange}
                label={t('pcsInformation.pcsProviderType')}
              />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('pcsInformation.lastName')}
                dataTestID='lastName'
                inputValue={lastName}
                onChangeAction={setLastNameOnChange}
              />
              <Validation id={PcsInformationConstants.SET_LASTNAME} />
            </IonCol>
            <IonCol className='picklist'>
              <SInput
                isPHI
                label={t('pcsInformation.firstName')}
                dataTestID='firstName'
                inputValue={firstName}
                onChangeAction={setFirstNameOnChange}
              />
              <Validation id={PcsInformationConstants.SET_FIRSTNAME} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                elementId='milesToHosp'
                dataTestId='MilesToHosp'
                values={milesToHosp.split('')}
                label={t('pcsInformation.milesToHosp')}
                validCharacters={numberPadKeysWithDecimal}
                onNumPadClick={setMilesToHospOnChange}
              />
              <Validation id={PcsInformationConstants.SET_MILESTOHOSP} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />
          <IonRow className='mt-05'>
            <IonCol>
              <IonRow className='ion-padding-horizontal' data-testid='Title'>
                <IonCol>
                  <IonLabel color='medium'>{t('pcsInformation.pcsSignedDateTime')}</IonLabel>
                </IonCol>
              </IonRow>

              <IonRow className='ion-padding-horizontal' id='pcsDateTime' data-testid='TimesRow'>
                <IonCol className='picklist' data-testid='Column'>
                  <NumberPad
                    elementId='pcsDateTime'
                    dataTestId='Time'
                    className='times-button'
                    placeholder={displaySeconds ? 'HH:MM:SS' : 'HH:MM'}
                    values={truncateSecondsIfNecessary(pcsSignedTime, displaySeconds).split('')}
                    onNumPadClick={setTimeOnChange}
                    onBlur={onBlur}
                  />
                </IonCol>

                <IonCol className='ion-text-center'>
                  <IonButton
                    data-testid='SetCurrentTime'
                    className='transform-none set-time-button'
                    fill='clear'
                    color='primary-alt'
                    onClick={setCurrentTimeOnChange}>
                    <IonIcon size='large' icon={stopwatchOutline} />
                    <IonLabel className='ion-hide-lg-down'>{t('vitals.details.form.AddCurrentTime')}</IonLabel>
                  </IonButton>
                </IonCol>

                <IonCol className='picklist times-row-end-button' data-testid='Column'>
                  <NumberPad
                    elementId='pcsDateTime'
                    dataTestId='Date'
                    className='times-button'
                    placeholder='MM/DD/YY'
                    values={pcsSignedDate.split('')}
                    onNumPadClick={dateOnChange}
                  />
                </IonCol>
              </IonRow>
            </IonCol>
          </IonRow>
          <IonRow>
            <Validation id={PcsInformationConstants.SET_PCSSIGNEDTIME} />
            <Validation id={PcsInformationConstants.SET_PCSSIGNEDDATE} />
          </IonRow>
        </IonGrid>
      </IonList>
    </>
  )
}

export default PcsInformationTablet
